/**
 * PipelineBoard - Merchant Acquisition Pipeline CRM
 * Main component with Board (Kanban), List, and Stats views.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  LayoutGrid, List, BarChart3, Search, Plus, Settings,
  RefreshCw, User, Mail, Clock, DollarSign, ChevronUp,
  ChevronDown, AlertCircle, Inbox, GripVertical,
  ArrowRight, TrendingUp, Target, Trophy, XCircle
} from 'lucide-react';
import './PipelineBoard.css';
import OpportunityDetail from './OpportunityDetail';
import CreateOpportunityModal from './CreateOpportunityModal';
import PipelineSettings from './PipelineSettings';

function PipelineBoard({ apiWithAccessControl }) {
  // --- State ---
  const [stages, setStages] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [stats, setStats] = useState(null);
  const [view, setView] = useState('board');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusMsg, setStatusMsg] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');

  // Drag & Drop
  const [dragState, setDragState] = useState({ dragging: false, id: null });
  const [dragOverStage, setDragOverStage] = useState(null);

  // Modals / Panels
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  // List sorting
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // --- Data Loading ---
  const loadBoard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiWithAccessControl('/admin/pipeline/opportunities?view=board');
      const data = await res.json();
      if (data.success !== false) {
        setStages(data.board || []);
      } else {
        setError(data.message || 'Failed to load pipeline board');
      }
    } catch (err) {
      setError('Failed to load pipeline board. Please try again.');
      console.error('Pipeline board load error:', err);
    } finally {
      setLoading(false);
    }
  }, [apiWithAccessControl]);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiWithAccessControl('/admin/pipeline/opportunities');
      const data = await res.json();
      if (data.success !== false) {
        setOpportunities(data.opportunities || []);
      } else {
        setError(data.message || 'Failed to load opportunities');
      }
    } catch (err) {
      setError('Failed to load opportunities. Please try again.');
      console.error('Pipeline list load error:', err);
    } finally {
      setLoading(false);
    }
  }, [apiWithAccessControl]);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiWithAccessControl('/admin/pipeline/stats');
      const data = await res.json();
      if (data.success !== false) {
        setStats(data.stats || null);
      } else {
        setError(data.message || 'Failed to load stats');
      }
    } catch (err) {
      setError('Failed to load pipeline stats. Please try again.');
      console.error('Pipeline stats load error:', err);
    } finally {
      setLoading(false);
    }
  }, [apiWithAccessControl]);

  useEffect(() => {
    if (showSettings) return;
    if (view === 'board') loadBoard();
    else if (view === 'list') loadList();
    else if (view === 'stats') loadStats();
  }, [view, showSettings, loadBoard, loadList, loadStats]);

  // --- Status Message Auto-dismiss ---
  useEffect(() => {
    if (statusMsg) {
      const timer = setTimeout(() => setStatusMsg(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [statusMsg]);

  // --- Drag & Drop ---
  const handleDragStart = (e, opportunityId) => {
    e.dataTransfer.setData('opportunityId', String(opportunityId));
    e.dataTransfer.effectAllowed = 'move';
    setDragState({ dragging: true, id: opportunityId });
  };

  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageId);
  };

  const handleDragLeave = (e) => {
    // Only clear if leaving the column itself, not entering a child
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverStage(null);
    }
  };

  const handleDrop = async (e, targetStageId) => {
    e.preventDefault();
    const oppId = e.dataTransfer.getData('opportunityId');
    setDragState({ dragging: false, id: null });
    setDragOverStage(null);

    if (!oppId) return;

    // Optimistic update
    setStages(prev => {
      const updated = prev.map(stage => ({
        ...stage,
        opportunities: stage.opportunities ? stage.opportunities.filter(o => String(o.id) !== String(oppId)) : []
      }));
      const sourceStage = prev.find(s => s.opportunities?.some(o => String(o.id) === String(oppId)));
      const opp = sourceStage?.opportunities?.find(o => String(o.id) === String(oppId));
      if (opp) {
        const target = updated.find(s => String(s.id) === String(targetStageId));
        if (target) {
          target.opportunities = [...(target.opportunities || []), { ...opp, stageId: targetStageId }];
        }
      }
      return updated;
    });

    try {
      await apiWithAccessControl(`/admin/pipeline/opportunities/${oppId}/stage`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stageId: targetStageId })
      });
      setStatusMsg({ type: 'success', text: 'Opportunity moved successfully' });
      loadBoard();
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Failed to move opportunity. Reverting.' });
      loadBoard();
    }
  };

  const handleDragEnd = () => {
    setDragState({ dragging: false, id: null });
    setDragOverStage(null);
  };

  // --- Open Detail ---
  const openDetail = (opp) => {
    setSelectedOpportunity(opp);
    setShowDetailPanel(true);
  };

  const closeDetail = () => {
    setShowDetailPanel(false);
    setSelectedOpportunity(null);
  };

  // --- Sorting (List View) ---
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // --- Filtered Opportunities (Board) ---
  const filteredStages = useMemo(() => {
    if (!searchQuery.trim()) return stages;
    const q = searchQuery.toLowerCase();
    return stages.map(stage => ({
      ...stage,
      opportunities: (stage.opportunities || []).filter(o =>
        (o.title || '').toLowerCase().includes(q) ||
        (o.contactName || '').toLowerCase().includes(q) ||
        (o.contactEmail || '').toLowerCase().includes(q) ||
        (o.source || '').toLowerCase().includes(q)
      )
    }));
  }, [stages, searchQuery]);

  // --- Filtered & Sorted Opportunities (List) ---
  const sortedOpportunities = useMemo(() => {
    let filtered = opportunities;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = opportunities.filter(o =>
        (o.title || '').toLowerCase().includes(q) ||
        (o.contactName || '').toLowerCase().includes(q) ||
        (o.contactEmail || '').toLowerCase().includes(q) ||
        (o.source || '').toLowerCase().includes(q)
      );
    }

    return [...filtered].sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'title': aVal = (a.title || '').toLowerCase(); bVal = (b.title || '').toLowerCase(); break;
        case 'contactName': aVal = (a.contactName || '').toLowerCase(); bVal = (b.contactName || '').toLowerCase(); break;
        case 'stage': aVal = (a.stageName || '').toLowerCase(); bVal = (b.stageName || '').toLowerCase(); break;
        case 'expectedValue': aVal = a.expectedValue || 0; bVal = b.expectedValue || 0; break;
        case 'source': aVal = (a.source || '').toLowerCase(); bVal = (b.source || '').toLowerCase(); break;
        case 'daysOpen': aVal = a.daysOpen || 0; bVal = b.daysOpen || 0; break;
        default: aVal = new Date(a.createdAt || 0).getTime(); bVal = new Date(b.createdAt || 0).getTime();
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [opportunities, searchQuery, sortField, sortDir]);

  // --- Format helpers ---
  const formatCurrency = (val) => {
    if (!val && val !== 0) return '--';
    return '₦' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // --- Refresh for current view ---
  const refresh = () => {
    if (view === 'board') loadBoard();
    else if (view === 'list') loadList();
    else if (view === 'stats') loadStats();
  };

  // --- After create/update ---
  const handleCreated = () => {
    setShowCreateModal(false);
    setStatusMsg({ type: 'success', text: 'Opportunity created successfully' });
    refresh();
  };

  const handleDetailUpdate = () => {
    refresh();
    // Reload detail if still open
    if (selectedOpportunity) {
      // The detail component manages its own state;
      // we just refresh the board/list data
    }
  };

  const handleStagesUpdated = () => {
    setShowSettings(false);
    setStatusMsg({ type: 'success', text: 'Pipeline stages updated' });
    refresh();
  };

  // --- Sort Icon ---
  const SortIcon = ({ field }) => {
    const isActive = sortField === field;
    const Icon = isActive && sortDir === 'desc' ? ChevronDown : ChevronUp;
    return (
      <span className={`mkt-pipe-sort-icon ${isActive ? 'active' : ''}`}>
        <Icon size={12} />
      </span>
    );
  };

  // --- Render: Settings Panel ---
  if (showSettings) {
    return (
      <div className="mkt-pipe-container">
        <PipelineSettings
          apiWithAccessControl={apiWithAccessControl}
          onClose={() => setShowSettings(false)}
          onStagesUpdated={handleStagesUpdated}
        />
      </div>
    );
  }

  // --- Render: Board View ---
  const renderBoardView = () => {
    if (loading) {
      return (
        <div className="mkt-pipe-loading">
          <div className="mkt-pipe-spinner" />
          <span>Loading pipeline...</span>
        </div>
      );
    }

    if (!filteredStages.length) {
      return (
        <div className="mkt-pipe-empty">
          <Inbox size={40} />
          <p>No pipeline stages configured</p>
          <span>Set up your pipeline stages in Settings to get started.</span>
        </div>
      );
    }

    const allEmpty = filteredStages.every(s => !s.opportunities || s.opportunities.length === 0);
    if (allEmpty && searchQuery.trim()) {
      return (
        <div className="mkt-pipe-empty">
          <Search size={40} />
          <p>No matching opportunities</p>
          <span>Try adjusting your search terms.</span>
        </div>
      );
    }

    return (
      <div className="mkt-pipe-board">
        {filteredStages.map(stage => {
          const opps = stage.opportunities || [];
          const stageValue = opps.reduce((sum, o) => sum + (o.expectedValue || 0), 0);
          const isDropTarget = dragOverStage === stage.id;

          return (
            <div
              key={stage.id}
              className={`mkt-pipe-column ${isDropTarget ? 'drag-over' : ''}`}
              onDragOver={(e) => handleDragOver(e, stage.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, stage.id)}
            >
              <div className="mkt-pipe-column-header">
                <span className="mkt-pipe-stage-dot" style={{ backgroundColor: stage.color || '#94a3b8' }} />
                <span className="mkt-pipe-column-name">{stage.name}</span>
                <span className="mkt-pipe-column-count">{opps.length}</span>
              </div>
              {stageValue > 0 && (
                <div className="mkt-pipe-column-value">
                  {formatCurrency(stageValue)} total
                </div>
              )}
              <div className="mkt-pipe-column-cards">
                {opps.length === 0 && (
                  <div style={{ padding: '16px 8px', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>
                    No opportunities
                  </div>
                )}
                {opps.map(opp => (
                  <div
                    key={opp.id}
                    className={`mkt-pipe-card ${dragState.dragging && String(dragState.id) === String(opp.id) ? 'dragging' : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, opp.id)}
                    onDragEnd={handleDragEnd}
                    onClick={() => openDetail(opp)}
                  >
                    <div className="mkt-pipe-card-title">{opp.title}</div>
                    {(opp.contactName || opp.contactEmail) && (
                      <div className="mkt-pipe-card-contact">
                        <User size={12} />
                        <span>{opp.contactName || opp.contactEmail}</span>
                      </div>
                    )}
                    <div className="mkt-pipe-card-meta">
                      {opp.expectedValue ? (
                        <span className="mkt-pipe-card-value">{formatCurrency(opp.expectedValue)}</span>
                      ) : (
                        <span />
                      )}
                      {opp.daysInStage != null && (
                        <span className="mkt-pipe-card-days">
                          <Clock size={11} />
                          {opp.daysInStage}d
                        </span>
                      )}
                    </div>
                    {opp.source && (
                      <div style={{ marginTop: '6px' }}>
                        <span className="mkt-pipe-card-source">{opp.source}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // --- Render: List View ---
  const renderListView = () => {
    if (loading) {
      return (
        <div className="mkt-pipe-loading">
          <div className="mkt-pipe-spinner" />
          <span>Loading opportunities...</span>
        </div>
      );
    }

    if (!sortedOpportunities.length) {
      return (
        <div className="mkt-pipe-empty">
          <Inbox size={40} />
          <p>{searchQuery ? 'No matching opportunities' : 'No opportunities yet'}</p>
          <span>{searchQuery ? 'Try adjusting your search terms.' : 'Create your first opportunity to get started.'}</span>
        </div>
      );
    }

    return (
      <div className="mkt-pipe-list-wrap">
        <table className="mkt-pipe-list">
          <thead>
            <tr>
              <th onClick={() => handleSort('title')}>
                Title <SortIcon field="title" />
              </th>
              <th onClick={() => handleSort('contactName')}>
                Contact <SortIcon field="contactName" />
              </th>
              <th onClick={() => handleSort('stage')}>
                Stage <SortIcon field="stage" />
              </th>
              <th onClick={() => handleSort('expectedValue')}>
                Value <SortIcon field="expectedValue" />
              </th>
              <th onClick={() => handleSort('source')}>
                Source <SortIcon field="source" />
              </th>
              <th onClick={() => handleSort('daysOpen')}>
                Days Open <SortIcon field="daysOpen" />
              </th>
              <th style={{ cursor: 'default' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedOpportunities.map(opp => (
              <tr key={opp.id} onClick={() => openDetail(opp)}>
                <td>
                  <span className="mkt-pipe-list-title">{opp.title}</span>
                </td>
                <td>
                  <div className="mkt-pipe-list-contact">
                    <span className="mkt-pipe-list-contact-name">{opp.contactName || '--'}</span>
                    {opp.contactEmail && (
                      <span className="mkt-pipe-list-contact-email">{opp.contactEmail}</span>
                    )}
                  </div>
                </td>
                <td>
                  <span className="mkt-pipe-stage-badge">
                    <span className="mkt-pipe-stage-badge-dot" style={{ backgroundColor: opp.stageColor || '#94a3b8' }} />
                    {opp.stageName || '--'}
                  </span>
                </td>
                <td>
                  <span className="mkt-pipe-list-value">
                    {opp.expectedValue ? formatCurrency(opp.expectedValue) : '--'}
                  </span>
                </td>
                <td>{opp.source || '--'}</td>
                <td>{opp.daysOpen != null ? `${opp.daysOpen} days` : '--'}</td>
                <td>
                  <div className="mkt-pipe-list-actions">
                    <button
                      className="mkt-pipe-btn-icon"
                      style={{ width: 28, height: 28 }}
                      title="View details"
                      onClick={(e) => { e.stopPropagation(); openDetail(opp); }}
                    >
                      <ArrowRight size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // --- Render: Stats View ---
  const renderStatsView = () => {
    if (loading) {
      return (
        <div className="mkt-pipe-loading">
          <div className="mkt-pipe-spinner" />
          <span>Loading statistics...</span>
        </div>
      );
    }

    if (!stats) {
      return (
        <div className="mkt-pipe-empty">
          <BarChart3 size={40} />
          <p>No statistics available</p>
          <span>Add some opportunities to see your pipeline stats.</span>
        </div>
      );
    }

    const maxCount = stats.countByStage
      ? Math.max(...stats.countByStage.map(s => s.count || 0), 1)
      : 1;

    const maxValue = stats.countByStage
      ? Math.max(...stats.countByStage.map(s => s.value || 0), 1)
      : 1;

    const totalStageCount = stats.countByStage
      ? stats.countByStage.reduce((sum, s) => sum + (s.count || 0), 0)
      : 0;

    return (
      <div className="mkt-pipe-stats-wrap">
        {/* Summary Cards */}
        <div className="mkt-pipe-stats-summary">
          <div className="mkt-pipe-stat-card mkt-pipe-stat-card--indigo">
            <div className="mkt-pipe-stat-card-icon mkt-pipe-stat-card-icon--indigo">
              <Inbox size={20} />
            </div>
            <div className="mkt-pipe-stat-card-body">
              <div className="mkt-pipe-stat-card-label">Total Opportunities</div>
              <div className="mkt-pipe-stat-card-value">{stats.totalOpportunities || 0}</div>
              <div className="mkt-pipe-stat-card-sub">
                <span className="mkt-pipe-stat-card-highlight mkt-pipe-stat-card-highlight--indigo">{stats.openOpportunities || 0}</span> currently open
              </div>
            </div>
          </div>
          <div className="mkt-pipe-stat-card mkt-pipe-stat-card--green">
            <div className="mkt-pipe-stat-card-icon mkt-pipe-stat-card-icon--green">
              <DollarSign size={20} />
            </div>
            <div className="mkt-pipe-stat-card-body">
              <div className="mkt-pipe-stat-card-label">Pipeline Value</div>
              <div className="mkt-pipe-stat-card-value">{formatCurrency(stats.totalValue || 0)}</div>
              <div className="mkt-pipe-stat-card-sub">
                Total expected value
              </div>
            </div>
          </div>
          <div className="mkt-pipe-stat-card mkt-pipe-stat-card--amber">
            <div className="mkt-pipe-stat-card-icon mkt-pipe-stat-card-icon--amber">
              <Target size={20} />
            </div>
            <div className="mkt-pipe-stat-card-body">
              <div className="mkt-pipe-stat-card-label">Win Rate</div>
              <div className="mkt-pipe-stat-card-value">
                {stats.winRate != null ? `${Number(stats.winRate).toFixed(1)}%` : '--'}
              </div>
              <div className="mkt-pipe-stat-card-sub">
                <span className="mkt-pipe-stat-card-highlight mkt-pipe-stat-card-highlight--green">{stats.wonCount || 0} won</span> / <span className="mkt-pipe-stat-card-highlight mkt-pipe-stat-card-highlight--red">{stats.lostCount || 0} lost</span>
              </div>
            </div>
          </div>
          <div className="mkt-pipe-stat-card mkt-pipe-stat-card--blue">
            <div className="mkt-pipe-stat-card-icon mkt-pipe-stat-card-icon--blue">
              <Clock size={20} />
            </div>
            <div className="mkt-pipe-stat-card-body">
              <div className="mkt-pipe-stat-card-label">Avg Days to Close</div>
              <div className="mkt-pipe-stat-card-value">
                {stats.avgDaysToClose != null ? Math.round(stats.avgDaysToClose) : '--'}
              </div>
              <div className="mkt-pipe-stat-card-sub">
                Average time to close
              </div>
            </div>
          </div>
        </div>

        {/* Stage Breakdown Chart */}
        {stats.countByStage && stats.countByStage.length > 0 && (
          <div className="mkt-pipe-stats-breakdown">
            <div className="mkt-pipe-stats-breakdown-header">
              <div>
                <h3>Pipeline by Stage</h3>
                <span className="mkt-pipe-stats-breakdown-subtitle">{totalStageCount} total across {stats.countByStage.length} stages</span>
              </div>
            </div>
            <div className="mkt-pipe-stats-breakdown-chart">
              {stats.countByStage.map(stage => {
                const pct = ((stage.count || 0) / maxCount) * 100;
                return (
                  <div key={stage.id || stage.name} className="mkt-pipe-stage-row">
                    <div className="mkt-pipe-stage-row-left">
                      <span className="mkt-pipe-stage-row-dot" style={{ backgroundColor: stage.color || '#94a3b8' }} />
                      <span className="mkt-pipe-stage-row-name">{stage.name}</span>
                    </div>
                    <div className="mkt-pipe-stage-bar-wrap">
                      <div
                        className="mkt-pipe-stage-bar"
                        style={{
                          width: `${Math.max(pct, 3)}%`,
                          background: `linear-gradient(135deg, ${stage.color || '#94a3b8'}, ${stage.color || '#94a3b8'}cc)`
                        }}
                      >
                        {pct > 18 && (
                          <span className="mkt-pipe-stage-bar-label">{stage.count || 0}</span>
                        )}
                      </div>
                      {pct <= 18 && (
                        <span className="mkt-pipe-stage-bar-label-outside">{stage.count || 0}</span>
                      )}
                    </div>
                    <div className="mkt-pipe-stage-row-right">
                      <span className="mkt-pipe-stage-row-value">{formatCurrency(stage.value || 0)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Won / Lost Outcome Cards */}
        <div className="mkt-pipe-stats-outcomes">
          <div className="mkt-pipe-outcome-card mkt-pipe-outcome-card--won">
            <div className="mkt-pipe-outcome-card-icon mkt-pipe-outcome-card-icon--won">
              <Trophy size={22} />
            </div>
            <div className="mkt-pipe-outcome-card-content">
              <span className="mkt-pipe-outcome-card-label">Deals Won</span>
              <span className="mkt-pipe-outcome-card-value mkt-pipe-outcome-card-value--won">{stats.wonCount || 0}</span>
            </div>
            <div className="mkt-pipe-outcome-card-bar">
              <div
                className="mkt-pipe-outcome-card-bar-fill mkt-pipe-outcome-card-bar-fill--won"
                style={{ width: `${stats.winRate != null ? Number(stats.winRate) : 0}%` }}
              />
            </div>
          </div>
          <div className="mkt-pipe-outcome-card mkt-pipe-outcome-card--lost">
            <div className="mkt-pipe-outcome-card-icon mkt-pipe-outcome-card-icon--lost">
              <XCircle size={22} />
            </div>
            <div className="mkt-pipe-outcome-card-content">
              <span className="mkt-pipe-outcome-card-label">Deals Lost</span>
              <span className="mkt-pipe-outcome-card-value mkt-pipe-outcome-card-value--lost">{stats.lostCount || 0}</span>
            </div>
            <div className="mkt-pipe-outcome-card-bar">
              <div
                className="mkt-pipe-outcome-card-bar-fill mkt-pipe-outcome-card-bar-fill--lost"
                style={{ width: `${stats.winRate != null ? (100 - Number(stats.winRate)) : 0}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // --- Render: View Content ---
  const renderContent = () => {
    switch (view) {
      case 'board': return renderBoardView();
      case 'list': return renderListView();
      case 'stats': return renderStatsView();
      default: return renderBoardView();
    }
  };

  return (
    <div className="mkt-pipe-container">
      {/* Header */}
      <div className="mkt-pipe-header">
        <div className="mkt-pipe-header-left">
          <h1 className="mkt-pipe-header-title">Pipeline</h1>
          <div className="mkt-pipe-view-toggle">
            <button
              className={`mkt-pipe-view-btn ${view === 'board' ? 'active' : ''}`}
              onClick={() => setView('board')}
            >
              <LayoutGrid size={14} />
              <span>Board</span>
            </button>
            <button
              className={`mkt-pipe-view-btn ${view === 'list' ? 'active' : ''}`}
              onClick={() => setView('list')}
            >
              <List size={14} />
              <span>List</span>
            </button>
            <button
              className={`mkt-pipe-view-btn ${view === 'stats' ? 'active' : ''}`}
              onClick={() => setView('stats')}
            >
              <BarChart3 size={14} />
              <span>Stats</span>
            </button>
          </div>
        </div>
        <div className="mkt-pipe-header-right">
          <div className="mkt-pipe-search-wrap">
            <Search size={14} className="mkt-pipe-search-icon" />
            <input
              type="text"
              className="mkt-pipe-search-input"
              placeholder="Search opportunities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            className="mkt-pipe-btn-icon"
            onClick={refresh}
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
          <button
            className="mkt-pipe-btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus size={15} />
            <span>New Opportunity</span>
          </button>
          <button
            className="mkt-pipe-btn-icon"
            onClick={() => setShowSettings(true)}
            title="Pipeline Settings"
          >
            <Settings size={16} />
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {statusMsg && (
        <div className={`mkt-pipe-status-msg ${statusMsg.type}`}>
          {statusMsg.type === 'success' ? <Target size={14} /> : <AlertCircle size={14} />}
          {statusMsg.text}
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="mkt-pipe-error">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={refresh}>Retry</button>
        </div>
      )}

      {/* Content */}
      {renderContent()}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateOpportunityModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          apiWithAccessControl={apiWithAccessControl}
          onCreated={handleCreated}
        />
      )}

      {/* Detail Panel */}
      {showDetailPanel && selectedOpportunity && (
        <OpportunityDetail
          opportunity={selectedOpportunity}
          isOpen={showDetailPanel}
          onClose={closeDetail}
          apiWithAccessControl={apiWithAccessControl}
          onUpdate={handleDetailUpdate}
        />
      )}
    </div>
  );
}

export default PipelineBoard;
